import { User } from "oidc-client-ts";
import { LookupTnService } from "../model/store/lookupTnService";
import {
  AuthState,
  QueryServiceData,
  queryServiceDataFactory,
} from "../store/authSlice";

const USER_PID_ATTRIBUTE_NAME = "provisionerId";
const USER_CNAM_CUSTOMER_ID_NAME = "cnamCustomerId";
const OPERATIONS_ENGINEER_ROLE = "Operations Engineer";
const OPERATIONS_VP_ROLE = "VP, Operations";
const LEA_PROFILE_NAME = "Law Enforcement Agent";

const USER_ROLE_ATTRIBUTE_NAME = "userRole";
const USER_PROFILE_ATTRIBUTE_NAME = "userProfile";
const CID_QUERY_SERVICE_SUFFIX_ATTRIBUTE_NAME = "cidClientSuffix";
const NCHECK_QUERY_SERVICE_SUFFIX_ATTRIBUTE_NAME = "ncheckClientSuffix";
const NVIEW_QUERY_SERVICE_SUFFIX_ATTRIBUTE_NAME = "nviewClientSuffix";

//TODO; Find type for this stuff?

/**
 * To test if a RAW OIDC Response object is an admin object. AKA, it has a "UserRole" set and that value
 * is either "Operations Engineer" / "VP, Operations"
 *
 * @export
 * @param {*} res The object returned by signinRedirectCallback
 * @return {boolean} true if the object IS an admin
 */
export function authResultIsAdmin(res: any): boolean {
  if (!res.profile.custom_attributes) return false;
  if (!res.profile.custom_attributes[USER_ROLE_ATTRIBUTE_NAME]) return false;

  const userRole = res.profile.custom_attributes[USER_ROLE_ATTRIBUTE_NAME];

  return (
    userRole === OPERATIONS_ENGINEER_ROLE || userRole === OPERATIONS_VP_ROLE
  );
}

export function authResultGetProfile(res: any): string {
  if (!res.profile.custom_attributes) return "";
  if (!res.profile.custom_attributes[USER_PROFILE_ATTRIBUTE_NAME]) return "";

  return res.profile.custom_attributes[USER_PROFILE_ATTRIBUTE_NAME];
}

/**
 * To test if a RAW OIDC Response object is an NNSR Portal USER! does NOT test for admin. AKA, does the
 * user have a provisionerId set
 *
 * @export
 * @param {*} res The object returned by signinRedirectCallback
 * @return {boolean} true if the object IS an nnsr portal user (Not an admin!)
 */
export function authResultIsPortalUser(res: any): boolean {
  if (!res.profile.custom_attributes) return false;
  if (!res.profile.custom_attributes[USER_PID_ATTRIBUTE_NAME]) return false;

  const userPid = res.profile.custom_attributes[USER_PID_ATTRIBUTE_NAME];

  return userPid !== null && userPid !== undefined && userPid.trim() !== "";
}

/**
 * To test if a RAW OIDC Response object is an Query Portal USER! does NOT test for admin. AKA, does the
 * user have any if it's 3 potential query service suffixes set
 *
 * @export
 * @param {*} res The object returned by signinRedirectCallback
 * @return {boolean} true if the object IS a query portal user (Not an admin!)
 */
export function authResultIsQueryUser(res: any): boolean {
  if (!res.profile.custom_attributes) return false;
  if (
    res.profile.custom_attributes.userProfile &&
    res.profile.custom_attributes.userProfile == "Law Enforcement Agent"
  ) {
    return true;
  }
  if (
    res.profile.custom_attributes.cidClientSuffix &&
    res.profile.custom_attributes.cidClientSuffix.trim() !== ""
  ) {
    return true;
  }
  if (
    res.profile.custom_attributes.ncheckClientSuffix &&
    res.profile.custom_attributes.ncheckClientSuffix.trim() !== ""
  ) {
    return true;
  }
  if (
    res.profile.custom_attributes.nviewClientSuffix &&
    res.profile.custom_attributes.nviewClientSuffix.trim() !== ""
  ) {
    return true;
  }
  return false;
}

export function authResultGetPid(res: any) {
  if (!res.profile.custom_attributes) return "";
  if (!res.profile.custom_attributes[USER_PID_ATTRIBUTE_NAME]) return "";

  return res.profile.custom_attributes[USER_PID_ATTRIBUTE_NAME];
}

export function authResultIsNumberNameUser(res: any): boolean {
  if (!res.profile.custom_attributes) return false;
  if (!res.profile.custom_attributes[USER_CNAM_CUSTOMER_ID_NAME]) return false;

  return true;
}

export function authResultGetCnamCustomerId(res: any) {
  if (!res.profile.custom_attributes) return "";
  if (!res.profile.custom_attributes[USER_CNAM_CUSTOMER_ID_NAME]) return "";

  return res.profile.custom_attributes[USER_CNAM_CUSTOMER_ID_NAME];
}

export function authResultGetQueryData(res: any): QueryServiceData {
  const result: QueryServiceData = queryServiceDataFactory();

  result.cidClientSuffix =
    res.profile.custom_attributes[CID_QUERY_SERVICE_SUFFIX_ATTRIBUTE_NAME];
  result.ncheckClientSuffix =
    res.profile.custom_attributes[NCHECK_QUERY_SERVICE_SUFFIX_ATTRIBUTE_NAME];
  result.nviewClientSuffix =
    res.profile.custom_attributes[NVIEW_QUERY_SERVICE_SUFFIX_ATTRIBUTE_NAME];

  return result;
}

export function getEnabledQueryServices(state: AuthState): LookupTnService[] {
  let result: LookupTnService[] = [];

  if (isFieldSet(state, "cidClientSuffix")) result.push("CID");
  if (isFieldSet(state, "ncheckClientSuffix")) result.push("NCHECK");
  if (isFieldSet(state, "nviewClientSuffix")) result.push("NVIEW");

  return result;
}

export function isQueryServicePortalEnabled(state: AuthState): boolean {
  return (
    isFieldSet(state, "cidClientSuffix") ||
    isFieldSet(state, "ncheckClientSuffix") ||
    isFieldSet(state, "nviewClientSuffix") ||
    isUserLawEnforcmentAgent(state.userProfile)
  );
}

export function isUserLawEnforcmentAgent(userProfile: string): boolean {
  return userProfile.includes(LEA_PROFILE_NAME);
}

function isFieldSet(state: AuthState, key: keyof QueryServiceData): boolean {
  return (
    state.queryServiceData[key] !== null &&
    state.queryServiceData[key] !== undefined &&
    state.queryServiceData[key]?.trim() !== ""
  );
}
